import {
  CheckIcon,
  ChevronRightIcon,
  ListFilterIcon,
  SearchIcon,
  SlidersHorizontalIcon,
  XIcon,
  type LucideIcon,
} from "lucide-react"
import {
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react"
import { Button } from "@/primitives/button.tsx"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/primitives/input-group.tsx"
import { Popover, PopoverContent, PopoverTrigger } from "@/primitives/popover.tsx"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/primitives/select.tsx"
import { Switch } from "@/primitives/switch.tsx"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { cn } from "@/primitives/utils"
import { useShortcut, useShortcuts } from "@/shortcuts/use-shortcut.ts"
import { Kbd } from "./key.tsx"
import { chipValuesOf, isOffered, operatorOf } from "./filter-bar.ts"
import { isSearchable, matching } from "./property.ts"
import { SearchRow } from "./property.tsx"
import { useTriggerTooltip } from "./use-trigger-tooltip.ts"

/*
 * A list's toolbar controls, one idiom on every page: Filter, which narrows the list by a facet and
 * leaves a removable chip saying so ("Role is Admin ×"); a search field; and Display, which holds
 * the view's options (order, grouping, what's included). None of them owns state: the page (or
 * the DataTable) keeps the view in the URL and passes it in, and hears every change.
 */

/**
 * One value a facet can take, as the Filter menu offers it. `count` is how many rows have it;
 * `keywords` are more words its search finds it by without showing them, as a Member's email.
 */
export type FacetOption = {
  id: string
  label: string
  glyph?: ReactNode
  secondary?: string
  keywords?: string
  count?: number
}

/**
 * One way to narrow a list: its values, the ones chosen, and whether one (`single`) or several
 * (`multi`, any of which passes) may be chosen at once. `labelOf` names a chosen value that isn't
 * among `values`, as one from an old link is. `narrowsWithOne` is a facet only some rows have a
 * value of, which the menu offers with one value alone.
 */
export type Facet = {
  id: string
  label: string
  icon: LucideIcon
  mode: "single" | "multi"
  values: readonly FacetOption[]
  chosen: readonly string[]
  onChange: (ids: readonly string[]) => void
  labelOf?: (id: string) => string
  narrowsWithOne?: boolean
}

/** Moves focus to the next or previous option in a menu with the arrow keys. */
function roveOptions(event: KeyboardEvent<HTMLElement>) {
  if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return
  const options = [...event.currentTarget.querySelectorAll<HTMLElement>("[data-option]")]
  if (options.length === 0) return
  event.preventDefault()
  const at = options.indexOf(document.activeElement as HTMLElement)
  const next =
    event.key === "ArrowDown"
      ? options[at < 0 ? 0 : Math.min(at + 1, options.length - 1)]
      : options[at < 0 ? options.length - 1 : Math.max(at - 1, 0)]
  next?.focus()
}

// A row in the Filter popover, highlighted in the popover's accent: the panel's own would vanish on
// a dark popover, and the keyboard's place in the list with it.
const optionClass =
  "flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-ui outline-none select-none transition-colors duration-100 ease-snappy hover:bg-popover-accent focus-visible:bg-popover-accent"

/**
 * Marks `element` `data-overflowing` while its content runs past its end, so it can fade there. It
 * is read after every render (a chip added or removed) and on every resize, and set on the DOM
 * rather than kept in state, so measuring never renders again.
 */
function useOverflowMark(element: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    const shown = element.current
    if (!shown) return
    const measure = () =>
      shown.toggleAttribute("data-overflowing", shown.scrollWidth > shown.clientWidth)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(shown)
    return () => observer.disconnect()
  })
}

/**
 * The Filter button, the chips for every facet with a value chosen, and "Clear" after them. The
 * menu opens on the facets (F opens it too); choosing one lists its values, with a search once
 * there are more than seven, and each value's count. Backspace in an empty search goes back to the
 * facets. A facet with too few values to narrow anything isn't offered (`isOffered`), though its
 * chip stays while a value is chosen (from an old link, say). A chip's value reopens its facet,
 * and Esc clears every filter when nothing is open over the page.
 *
 * `onClear` clears every facet at once, which the page does in one change to its URL.
 */
export function FilterMenu({ facets, onClear }: { facets: readonly Facet[]; onClear: () => void }) {
  const [open, setOpen] = useState(false)
  const [level, setLevel] = useState<string | null>(null)
  const offered = facets.filter(isOffered)
  const chosen = facets.filter((facet) => facet.chosen.length > 0)
  const facet = facets.find(({ id }) => id === level)
  const chips = useRef<HTMLDivElement>(null)
  useOverflowMark(chips)
  const tip = useTriggerTooltip(open)

  const openAt = (id: string | null) => {
    setLevel(id)
    setOpen(true)
  }

  useShortcut("open-filter", () => openAt(null), { enabled: offered.length > 0 })
  useShortcuts({ "clear-filters": onClear }, { enabled: chosen.length > 0 })

  return (
    <>
      {offered.length > 0 && (
        <Popover
          open={open}
          onOpenChange={setOpen}
          onOpenChangeComplete={(isOpen) => !isOpen && setLevel(null)}
        >
          <Tooltip {...tip.tooltip}>
            <TooltipTrigger
              render={
                <PopoverTrigger
                  render={<Button variant="ghost" size="sm" onClick={() => setLevel(null)} />}
                />
              }
              {...tip.trigger}
            >
              <ListFilterIcon data-icon="inline-start" />
              Filter
            </TooltipTrigger>
            <TooltipContent>
              Filter
              <Kbd shortcut="open-filter" />
            </TooltipContent>
          </Tooltip>
          <PopoverContent align="start" className="w-64 gap-0 p-1">
            {facet ? (
              <FacetValues
                key={facet.id}
                facet={facet}
                onBack={() => setLevel(null)}
                onChosen={() => setOpen(false)}
              />
            ) : (
              <div role="menu" aria-label="Filter by" onKeyDown={roveOptions}>
                {offered.map(({ id, label, icon: Icon }, index) => (
                  <button
                    key={id}
                    type="button"
                    role="menuitem"
                    data-option
                    autoFocus={index === 0}
                    className={optionClass}
                    onClick={() => setLevel(id)}
                  >
                    <Icon aria-hidden className="size-4 text-muted-foreground" />
                    <span className="truncate">{label}</span>
                    <ChevronRightIcon
                      aria-hidden
                      className="ml-auto size-3.5 text-subtle-foreground"
                    />
                  </button>
                ))}
              </div>
            )}
          </PopoverContent>
        </Popover>
      )}
      {chosen.length > 0 && (
        // The chips give way before anything else in the toolbar: past what the row has room for
        // they clip under a fade at its end, so a Board with every facet set never runs under the
        // search or Display. A chip clipped away is still in the Filter menu, and Tab scrolls to
        // it. Clear stays outside, never clipped; the padding keeps the chips' rings and focus
        // rings clear of the clipping edge. (A chip keeps its whole width: a flex item can't
        // shrink past its text without spilling its field and ×.)
        <div
          ref={chips}
          className="-mx-1 flex min-w-0 items-center gap-2 overflow-hidden px-1 py-1 data-overflowing:fade-x-end"
        >
          {chosen.map((each) => (
            <FilterChip
              key={each.id}
              field={{ label: each.label, icon: each.icon }}
              operator={operatorOf(each.mode, each.chosen.length)}
              values={chipValuesOf(each.values, each.chosen, each.labelOf)}
              onRemove={() => each.onChange([])}
              {...(offered.includes(each) && { onOpen: () => openAt(each.id) })}
            />
          ))}
        </div>
      )}
      {chosen.length > 0 && (
        <Button variant="ghost" size="xs" onClick={onClear}>
          Clear
        </Button>
      )}
    </>
  )
}

/** A facet's values, the second level of the Filter menu. */
function FacetValues({
  facet,
  onBack,
  onChosen,
}: {
  facet: Facet
  onBack: () => void
  onChosen: () => void
}) {
  const [query, setQuery] = useState("")
  const searchable = isSearchable(facet.values)
  const shown = matching(facet.values, query)
  const multi = facet.mode === "multi"

  const toggle = (id: string) => {
    if (!multi) {
      facet.onChange(facet.chosen.includes(id) ? [] : [id])
      onChosen()
      return
    }
    facet.onChange(
      facet.chosen.includes(id)
        ? facet.chosen.filter((each) => each !== id)
        : [...facet.chosen, id],
    )
  }

  return (
    <div onKeyDown={roveOptions}>
      {searchable && (
        // The popover's padding is the menu's; the search runs edge to edge above it, as a
        // Picker's does.
        <div className="-mx-1 -mt-1 mb-1">
          <SearchRow
            autoFocus
            aria-label={`Search ${facet.label}`}
            placeholder={`${facet.label}…`}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Backspace" && query === "") onBack()
            }}
          />
        </div>
      )}
      <div
        role="menu"
        aria-label={facet.label}
        className="max-h-80 overflow-y-auto"
        onKeyDown={(event) => {
          if (event.key === "Backspace") onBack()
        }}
      >
        {shown.length === 0 ? (
          <p className="py-6 text-center text-ui text-subtle-foreground">No matches</p>
        ) : (
          shown.map((value, index) => {
            const checked = facet.chosen.includes(value.id)
            return (
              <button
                key={value.id}
                type="button"
                role={multi ? "menuitemcheckbox" : "menuitemradio"}
                aria-checked={checked}
                data-option
                autoFocus={!searchable && index === 0}
                className={optionClass}
                onClick={() => toggle(value.id)}
              >
                {multi ? (
                  <span
                    aria-hidden
                    className={cn(
                      "flex size-3.5 shrink-0 items-center justify-center rounded-sm border",
                      checked
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-subtle-foreground bg-background",
                    )}
                  >
                    {checked && <CheckIcon className="size-3 stroke-3" />}
                  </span>
                ) : (
                  <span aria-hidden className="flex size-4 shrink-0 items-center justify-center">
                    {checked && <CheckIcon className="size-3.5 text-foreground" />}
                  </span>
                )}
                {value.glyph}
                <span className="truncate">
                  {value.label}
                  {value.secondary && (
                    <span className="ml-1.5 text-subtle-foreground">{value.secondary}</span>
                  )}
                </span>
                {value.count !== undefined && (
                  <span className="ml-auto pl-2 text-xs text-subtle-foreground tabular-nums">
                    {value.count}
                  </span>
                )}
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}

const segment = "flex h-full items-center gap-1 px-1.5"

/**
 * One filter in force, in three parts: the field, the operator ("is", "is any of") and the value,
 * its glyphs then its first two names and "+N" for the rest; then × to remove it. `onOpen` makes
 * the value a button that reopens the facet's values. A chip for a view choice rather than a
 * facet, such as "Closed included", passes neither `field` nor `operator`, and is its value alone.
 */
export function FilterChip({
  field,
  operator,
  values,
  onRemove,
  onOpen,
}: {
  field?: { label: string; icon: LucideIcon }
  operator?: "is" | "is any of"
  values: readonly { label: string; glyph?: ReactNode }[]
  onRemove: () => void
  onOpen?: () => void
}) {
  const named = values.slice(0, 2)
  const rest = values.length - named.length
  const value = (
    <>
      {values.some(({ glyph }) => glyph) && (
        <span className="flex items-center gap-0.5">
          {values
            .slice(0, 3)
            .map(({ glyph }, index) => (glyph ? <span key={index}>{glyph}</span> : null))}
        </span>
      )}
      <span className="max-w-40 truncate">
        {named.map(({ label }) => label).join(", ")}
        {rest > 0 && ` +${rest}`}
      </span>
    </>
  )
  const described = [field?.label, operator, values.map(({ label }) => label).join(", ")]
    .filter(Boolean)
    .join(" ")
  return (
    <span
      role="group"
      aria-label={described}
      className="inline-flex h-6 shrink-0 items-center rounded-md bg-background text-xs ring-1 ring-border"
    >
      {field && (
        <span className={cn(segment, "text-muted-foreground")}>
          <field.icon aria-hidden className="size-3.5" />
          {field.label}
        </span>
      )}
      {operator && (
        <span className={cn(segment, "border-l border-border-subtle text-subtle-foreground")}>
          {operator}
        </span>
      )}
      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          className={cn(
            segment,
            field && "border-l border-border-subtle",
            "text-foreground transition-colors duration-100 ease-snappy outline-none hover:bg-accent focus-visible:bg-accent",
          )}
        >
          {value}
        </button>
      ) : (
        <span className={cn(segment, field && "border-l border-border-subtle", "text-foreground")}>
          {value}
        </span>
      )}
      <Button
        variant="ghost"
        size="icon-xs"
        aria-label="Remove filter"
        onClick={onRemove}
        className="size-6 rounded-l-none border-l border-border-subtle"
      >
        <XIcon className="size-3" />
      </Button>
    </span>
  )
}

/**
 * The list's search: `/` focuses it from anywhere on the page, Esc empties it, and a second Esc
 * leaves it. The page debounces what it does with `onChange`, as its search needs.
 */
export function SearchField({
  placeholder,
  value,
  onChange,
}: {
  placeholder: string
  value: string
  onChange: (value: string) => void
}) {
  const field = useRef<HTMLInputElement>(null)
  useShortcut("search", () => {
    field.current?.focus()
    field.current?.select()
  })
  return (
    <InputGroup className="h-7 w-56 min-w-40">
      <InputGroupAddon>
        <SearchIcon />
      </InputGroupAddon>
      <InputGroupInput
        ref={field}
        aria-label={placeholder}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Escape") return
          if (value) onChange("")
          else event.currentTarget.blur()
        }}
      />
      <InputGroupAddon align="inline-end">
        {value ? (
          <InputGroupButton
            size="icon-xs"
            aria-label="Clear search"
            onClick={() => {
              onChange("")
              field.current?.focus()
            }}
          >
            <XIcon />
          </InputGroupButton>
        ) : (
          <span className="group-focus-within/input-group:hidden">
            <Kbd shortcut="search" />
          </span>
        )}
      </InputGroupAddon>
    </InputGroup>
  )
}

/** One of a view's options in the Display menu: a choice among several, or on and off. */
export type DisplayRow = {
  label: string
  control:
    | {
        kind: "select"
        value: string
        options: readonly { value: string; label: string }[]
        onChange: (value: string) => void
      }
    | {
        kind: "switch"
        checked: boolean
        onChange: (checked: boolean) => void
        /** Why the option can't be changed in this view, such as "Only when grouped by Epic". */
        disabledReason?: string
      }
}

/**
 * The view's options, behind one button: order, grouping and what's included. A dot on the button
 * says the view isn't the default, so a Member whose list looks short can tell something is set. An
 * option that doesn't apply to the view as it is shows faded, its tooltip saying why.
 */
export function DisplayMenu({
  rows,
  nonDefault,
}: {
  rows: readonly DisplayRow[]
  nonDefault: boolean
}) {
  return (
    <Popover>
      <PopoverTrigger render={<Button variant="outline" size="sm" className="relative" />}>
        <SlidersHorizontalIcon data-icon="inline-start" />
        Display
        {nonDefault && (
          <span
            aria-label="Changed from the default"
            className="absolute -top-0.5 -right-0.5 size-1.5 rounded-full bg-primary"
          />
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 gap-1 p-3">
        {rows.map((row) => (
          <DisplayOption key={row.label} row={row} />
        ))}
      </PopoverContent>
    </Popover>
  )
}

function DisplayOption({ row: { label, control } }: { row: DisplayRow }) {
  if (control.kind === "select")
    return (
      <div className="flex h-8 items-center justify-between gap-3 text-ui">
        <span className="text-muted-foreground">{label}</span>
        <Select
          items={control.options}
          value={control.value}
          onValueChange={(value) => value !== null && control.onChange(value)}
        >
          <SelectTrigger size="sm" variant="ghost" aria-label={label}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent align="end">
            {control.options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )
  const row = (
    <label
      className={cn(
        "flex h-8 items-center justify-between gap-3 text-ui",
        control.disabledReason && "opacity-50",
      )}
    >
      <span className="text-muted-foreground">{label}</span>
      <Switch
        checked={control.checked}
        disabled={Boolean(control.disabledReason)}
        onCheckedChange={(checked) => control.onChange(checked)}
      />
    </label>
  )
  if (!control.disabledReason) return row
  return (
    <Tooltip>
      <TooltipTrigger render={row} />
      <TooltipContent>{control.disabledReason}</TooltipContent>
    </Tooltip>
  )
}
